import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, IconButton, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import React, { Component } from 'react';
import FundOpportunity from '../../models/FundOpportunity';
import { IRRUpdate } from '../../models/IRRUpdate';
import { formatNumber } from '../../utils/formatNumber';
import StyledModal from '../styles/StyledModal';
import StyledSection from '../styles/StyledSection';
import { StyledHeading, StyledTypography } from '../styles/styles';

export interface IRRMetricsProps {
  title: string;
  expandedTitle: string;
  field: keyof IRRUpdate;
  description: string;
  fundOpportunity: FundOpportunity;
  irrUpdates: IRRUpdate[];
  unit: '%' | 'x';
  imValue: number | string;
  /** Admin's Fund Management preview renders this tile smaller, to suit the narrower admin content column. */
  canEdit?: boolean;
}

class IRRMetrics extends Component<
  IRRMetricsProps,
  {
    showModal: boolean;
  }
> {
  constructor(props: IRRMetricsProps) {
    super(props);
    this.state = { showModal: false };
  }

  render() {
    const { canEdit = false } = this.props;
    const currentIRRUpdate = this.props.irrUpdates.slice(-1)[0];
    const rawValue = currentIRRUpdate[this.props.field];
    const scale = this.props.unit === '%' ? 100 : 1;
    const value = typeof rawValue === 'number' ? rawValue * scale : rawValue;
    const month = format(new Date(`${currentIRRUpdate.month}-01`), 'MMMM yyyy');
    const allPreviousValues = this.props.irrUpdates.slice(0, -1).map((update) => {
      const fieldValue = update[this.props.field];
      const scaledValue = typeof fieldValue === 'number' ? fieldValue * scale : fieldValue;
      const formattedValue =
        scaledValue !== null && scaledValue !== undefined && typeof scaledValue !== 'boolean'
          ? formatNumber(scaledValue)
          : String(scaledValue ?? '');
      return `${format(new Date(`${update.month}-01`), 'MMMM yyyy')}: ${formattedValue}${this.props.unit}`;
    });

    return (
      <>
        <StyledSection
          tone="tone1"
          sx={{ px: { xs: 0 }, py: { xs: 0 }, flex: canEdit ? 1 : undefined }}
        >
          <StyledSection
            child
            noSidePaddingFrom="xs"
            tone="tone3"
            sx={{
              minWidth: canEdit ? '9rem' : '21.875rem',
              maxWidth: canEdit ? undefined : { md: '21.875rem' },
              maxHeight: canEdit ? undefined : '9.375rem',
              flex: canEdit ? 1 : undefined,
              pt: canEdit ? '0.4rem' : '0.5rem',
              pb: canEdit ? '0.5rem' : '0.8rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              lineHeight: 1,
            }}
            key={`${this.props.fundOpportunity.id}-${this.props.title}-${value}`}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                position: 'relative',
              }}
            >
              <StyledHeading
                headerVariant="heading2"
                sx={{ pt: 0, pb: 0, fontSize: canEdit ? '1.1rem' : undefined }}
              >
                {this.props.title}
              </StyledHeading>

              <Tooltip
                title="Click for help and additional information"
                arrow
                placement="top"
                disableHoverListener={false}
                disableFocusListener={false}
                disableTouchListener={false}
                enterTouchDelay={0}
                leaveTouchDelay={3000}
              >
                <IconButton
                  onClick={() => this.setState({ showModal: true })}
                  aria-label={`${this.props.title} help and additional information`}
                  size={canEdit ? 'small' : 'medium'}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    py: 0,
                  }}
                >
                  <HelpOutlineIcon fontSize={canEdit ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
            </Box>

            <StyledHeading
              headerVariant="heroValue"
              sx={{ pt: 0, pb: 0, fontSize: canEdit ? '1.75rem' : '2.5rem' }}
            >
              {value !== null && value !== undefined && typeof value !== 'boolean'
                ? formatNumber(value as number | string)
                : value}
              {this.props.unit}
            </StyledHeading>

            <StyledHeading
              headerVariant="subheading"
              sx={{ pt: 0, pb: 0, fontSize: canEdit ? '0.8rem' : undefined }}
            >
              {this.props.description}
            </StyledHeading>

            <StyledTypography sx={{ fontSize: canEdit ? '0.7rem' : '0.8rem' }}>
              IM Forecast:{' '}
              <StyledTypography
                component="span"
                sx={{ fontWeight: 600, fontSize: canEdit ? '0.7rem' : '0.8rem' }}
              >
                {formatNumber(this.props.imValue)}
                {this.props.unit}
              </StyledTypography>
            </StyledTypography>
          </StyledSection>
        </StyledSection>

        <StyledModal
          open={this.state.showModal}
          onClose={() => this.setState({ showModal: false })}
          fullWidth
          maxWidth="xs"
          tone="tone1"
        >
          <StyledHeading headerVariant="heading2" sx={{ textAlign: 'center' }}>
            {this.props.expandedTitle}
          </StyledHeading>

          <StyledSection
            tone="tone1"
            noTopPadding
            noSidePaddingFrom="xs"
            bottomSpacing={0}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
            }}
          >
            <Box
              sx={{
                px: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                rowGap: '2rem',
              }}
            >
              {[
                {
                  label: 'Current Forecast',
                  value: `${month}: ${value && !(typeof value === 'boolean') ? formatNumber(value) : value}${this.props.unit}`,
                },
                {
                  label: 'IM Forecast',
                  value: `${formatNumber(this.props.imValue)}${this.props.unit}`,
                },
                ...(allPreviousValues.length
                  ? [
                      {
                        label: 'Historical Forecasts',
                        value: allPreviousValues,
                      },
                    ]
                  : []),
              ].map(({ label, value: modalValue }) => (
                <Box
                  key={label}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={4}
                >
                  <StyledTypography fontWeight={600} fontSize="0.875rem">
                    {label}
                  </StyledTypography>
                  <Box textAlign="right">
                    {[modalValue].flat().map((v, i) => (
                      <StyledTypography fontSize="0.875rem" key={`${label}-${i}`}>
                        {v}
                      </StyledTypography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </StyledSection>
        </StyledModal>
      </>
    );
  }
}

export default IRRMetrics;
